/**
 * Cosmic Constellation Background
 * Custom canvas-based starfield with interactive constellation lines.
 *
 * Features:
 *  - Stars with size/opacity variation and twinkling
 *  - Constellation lines drawn between nearby stars
 *  - Mouse cursor attracts stars, lines form reactively
 *  - Nebula glow spots in purple/violet
 *  - Respects prefers-reduced-motion
 */

(function () {
  'use strict';

  // ─── Config ──────────────────────────────────────────────────────────────────
  const CFG = {
    starCount:        180,
    starMinR:         0.4,
    starMaxR:         2.2,
    starSpeed:        0.18,          // base drift speed
    lineDistance:     140,           // px — max distance to draw a line
    lineOpacityMax:   0.28,
    mouseRadius:      160,           // px — attraction zone
    mouseForce:       0.012,         // pull strength toward cursor
    nebulaCount:      5,
    fpsTarget:        60,

    colors: {
      bg:             '#05040c',
      starPrimary:    '#ffffff',
      starAccent:     '#c4b0ff',      // lavender stars
      linePrimary:    '#7b5cff',
      lineAccent:     '#a67cff',
      nebula: [
        { r: 123, g:  92, b: 255 },  // primary purple
        { r: 166, g: 124, b: 255 },  // accent lavender
        {  r: 70, g:  50, b: 140 },  // deep violet
      ],
    },
  };

  // ─── State ────────────────────────────────────────────────────────────────────
  let canvas, ctx, W, H, dpr;
  let stars  = [];
  let nebulae = [];
  let mouse  = { x: -9999, y: -9999 };
  let raf    = null;
  let last   = 0;
  let reduced = false;

  // Cursor glow state
  const glow = {
    alpha:       0,       // current opacity (lerped)
    targetAlpha: 0,       // 1 when mouse on screen, 0 when off
    pulse:       0,       // phase for breathing animation [0, 2π]
    outerRadius: 40,
    innerRadius: 12,
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  function rand(min, max) { return min + Math.random() * (max - min); }
  function lerp(a, b, t)  { return a + (b - a) * t; }

  // ─── Star ─────────────────────────────────────────────────────────────────────
  function createStar() {
    const isAccent = Math.random() < 0.18;
    return {
      x:       rand(0, W),
      y:       rand(0, H),
      r:       rand(CFG.starMinR, CFG.starMaxR),
      vx:      rand(-CFG.starSpeed, CFG.starSpeed),
      vy:      rand(-CFG.starSpeed, CFG.starSpeed),
      opacity: rand(0.4, 1.0),
      twinkleSpeed: rand(0.004, 0.018),
      twinkleDir:   Math.random() < 0.5 ? 1 : -1,
      color:   isAccent ? CFG.colors.starAccent : CFG.colors.starPrimary,
    };
  }

  // ─── Nebula ───────────────────────────────────────────────────────────────────
  function createNebula() {
    const c = CFG.colors.nebula[Math.floor(Math.random() * CFG.colors.nebula.length)];
    return {
      x:      rand(0.1 * W, 0.9 * W),
      y:      rand(0.1 * H, 0.9 * H),
      rx:     rand(W * 0.12, W * 0.28),
      ry:     rand(H * 0.10, H * 0.22),
      alpha:  rand(0.025, 0.07),
      r: c.r, g: c.g, b: c.b,
    };
  }

  // ─── Init ─────────────────────────────────────────────────────────────────────
  function init() {
    canvas = document.getElementById('cosmic-canvas');
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.id = 'cosmic-canvas';
      canvas.style.cssText = [
        'position:fixed',
        'inset:0',
        'width:100%',
        'height:100%',
        'z-index:-1',
        'pointer-events:none',
        'display:block',
      ].join(';');
      document.body.prepend(canvas);
    }
    ctx = canvas.getContext('2d');
    reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    resize();
    buildScene();
    bindEvents();
    if (!reduced) {
      raf = requestAnimationFrame(loop);
    } else {
      drawStatic();
    }
  }

  function buildScene() {
    // Scale star count proportionally to screen area so mobile isn't overcrowded
    const refArea    = 1440 * 900;
    const scaled     = Math.round(CFG.starCount * (W * H) / refArea);
    const count      = Math.max(50, Math.min(scaled, CFG.starCount));
    stars   = Array.from({ length: count }, createStar);
    nebulae = Array.from({ length: CFG.nebulaCount }, createNebula);
  }

  function resize() {
    dpr = window.devicePixelRatio || 1;
    W   = window.innerWidth;
    H   = window.innerHeight;
    canvas.width  = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width  = W + 'px';
    canvas.style.height = H + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  // ─── Events ───────────────────────────────────────────────────────────────────
  function bindEvents() {
    window.addEventListener('resize', () => {
      resize();
      buildScene();
    }, { passive: true });

    window.addEventListener('mousemove', (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      glow.targetAlpha = 1;
    }, { passive: true });

    window.addEventListener('touchmove', (e) => {
      const t = e.touches[0];
      mouse.x = t.clientX;
      mouse.y = t.clientY;
    }, { passive: true });

    window.addEventListener('mouseleave', () => {
      mouse.x = -9999;
      mouse.y = -9999;
      glow.targetAlpha = 0;
    }, { passive: true });
  }

  // ─── Draw ─────────────────────────────────────────────────────────────────────
  function drawBackground() {
    ctx.fillStyle = CFG.colors.bg;
    ctx.fillRect(0, 0, W, H);
  }

  function drawNebulae() {
    nebulae.forEach((n) => {
      const grad = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, Math.max(n.rx, n.ry));
      grad.addColorStop(0,   `rgba(${n.r},${n.g},${n.b},${n.alpha})`);
      grad.addColorStop(0.5, `rgba(${n.r},${n.g},${n.b},${n.alpha * 0.4})`);
      grad.addColorStop(1,   `rgba(${n.r},${n.g},${n.b},0)`);
      ctx.save();
      ctx.translate(n.x, n.y);
      ctx.scale(n.rx / Math.max(n.rx, n.ry), n.ry / Math.max(n.rx, n.ry));
      ctx.translate(-n.x, -n.y);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(n.x, n.y, Math.max(n.rx, n.ry), 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    });
  }

  function drawLines() {
    const distSq = CFG.lineDistance * CFG.lineDistance;
    for (let i = 0; i < stars.length; i++) {
      for (let j = i + 1; j < stars.length; j++) {
        const dx = stars[i].x - stars[j].x;
        const dy = stars[i].y - stars[j].y;
        const d2 = dx * dx + dy * dy;
        if (d2 > distSq) continue;
        const t       = 1 - d2 / distSq;
        const opacity = t * CFG.lineOpacityMax;
        // Alternate between primary and accent line colour for variety
        const useAccent = (i + j) % 3 === 0;
        const hex = useAccent ? CFG.colors.lineAccent : CFG.colors.linePrimary;
        ctx.strokeStyle = hexAlpha(hex, opacity);
        ctx.lineWidth   = t * 0.8;
        ctx.beginPath();
        ctx.moveTo(stars[i].x, stars[i].y);
        ctx.lineTo(stars[j].x, stars[j].y);
        ctx.stroke();
      }
    }
  }

  function drawStars() {
    stars.forEach((s) => {
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
      ctx.fillStyle = hexAlpha(s.color, s.opacity);
      ctx.fill();

      // Glow for larger stars
      if (s.r > 1.4) {
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r * 2.5, 0, Math.PI * 2);
        ctx.fillStyle = hexAlpha(s.color, s.opacity * 0.12);
        ctx.fill();
      }
    });
  }

  function drawCursorGlow() {
    if (glow.alpha < 0.005 || mouse.x === -9999) return;

    const pulse  = Math.sin(glow.pulse) * 0.12 + 0.88; // gentle 0.76–1.00 scale
    const outerR = glow.outerRadius * pulse;
    const innerR = glow.innerRadius * pulse;
    const a      = glow.alpha;

    // Outer soft halo
    const outer = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, outerR);
    outer.addColorStop(0,   `rgba(123,92,255,${(a * 0.10).toFixed(3)})`);
    outer.addColorStop(0.5, `rgba(123,92,255,${(a * 0.04).toFixed(3)})`);
    outer.addColorStop(1,   `rgba(123,92,255,0)`);
    ctx.fillStyle = outer;
    ctx.beginPath();
    ctx.arc(mouse.x, mouse.y, outerR, 0, Math.PI * 2);
    ctx.fill();

    // Inner bright core
    const inner = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, innerR);
    inner.addColorStop(0,   `rgba(210,195,255,${(a * 0.30).toFixed(3)})`);
    inner.addColorStop(0.5, `rgba(166,124,255,${(a * 0.14).toFixed(3)})`);
    inner.addColorStop(1,   `rgba(123,92,255,0)`);
    ctx.fillStyle = inner;
    ctx.beginPath();
    ctx.arc(mouse.x, mouse.y, innerR, 0, Math.PI * 2);
    ctx.fill();
  }

  // ─── Update ───────────────────────────────────────────────────────────────────
  function updateStars(dt) {
    const clampedDt = Math.min(dt, 50); // cap at 50 ms to avoid big jumps
    stars.forEach((s) => {
      // Mouse attraction
      const dx = mouse.x - s.x;
      const dy = mouse.y - s.y;
      const d2 = dx * dx + dy * dy;
      if (d2 < CFG.mouseRadius * CFG.mouseRadius && d2 > 1) {
        const d    = Math.sqrt(d2);
        const force = CFG.mouseForce * (1 - d / CFG.mouseRadius);
        s.vx = lerp(s.vx, s.vx + (dx / d) * force * clampedDt, 0.15);
        s.vy = lerp(s.vy, s.vy + (dy / d) * force * clampedDt, 0.15);
      }

      // Speed cap
      const spd = Math.sqrt(s.vx * s.vx + s.vy * s.vy);
      const maxSpd = CFG.starSpeed * 4;
      if (spd > maxSpd) {
        s.vx = (s.vx / spd) * maxSpd;
        s.vy = (s.vy / spd) * maxSpd;
      }

      // Drift
      s.x += s.vx * (clampedDt / 16);
      s.y += s.vy * (clampedDt / 16);

      // Velocity damping (drift back to natural speed)
      s.vx *= 0.995;
      s.vy *= 0.995;

      // Twinkling
      s.opacity += s.twinkleSpeed * s.twinkleDir;
      if (s.opacity >= 1.0) { s.opacity = 1.0; s.twinkleDir = -1; }
      if (s.opacity <= 0.2) { s.opacity = 0.2; s.twinkleDir =  1; }

      // Wrap edges
      if (s.x < -10) s.x = W + 10;
      if (s.x > W + 10) s.x = -10;
      if (s.y < -10) s.y = H + 10;
      if (s.y > H + 10) s.y = -10;
    });
  }

  // ─── Loop ─────────────────────────────────────────────────────────────────────
  function loop(ts) {
    const dt = ts - last;
    last = ts;
    raf = requestAnimationFrame(loop);

    // Update glow state
    glow.alpha = lerp(glow.alpha, glow.targetAlpha, 0.06);
    glow.pulse += 0.022;

    drawBackground();
    drawNebulae();
    drawCursorGlow();
    drawLines();
    drawStars();
    updateStars(dt);
  }

  function drawStatic() {
    drawBackground();
    drawNebulae();
    drawLines();
    drawStars();
  }

  // ─── Util ─────────────────────────────────────────────────────────────────────
  function hexAlpha(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha.toFixed(3)})`;
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
